import { useState } from "react";
import { CRITERIA, DEFAULT_WEIGHTS } from "../lib/criteria.js";
import { BuyingChecklist } from "./BuyingChecklist.jsx";
import { ProfileSwitcher } from "./ProfileSwitcher.jsx";
import { getProfiles } from "../lib/profiles.js";

const GROUPS = [
  { id: "family", icon: "🏡", label: "Voor het gezin",
    criteria: ["youngFamilies", "landBudget", "services", "montessori", "forestSchool"] },
  { id: "climate", icon: "🌡", label: "Klimaat",
    criteria: ["climate", "dryness", "sunshine"] },
  { id: "lifestyle", icon: "🌊", label: "Lifestyle",
    criteria: ["coffee", "wellness", "surf"] },
  { id: "slow", icon: "🌿", label: "Slow-living",
    criteria: ["natureAccess", "tranquility", "culturalAuthenticity", "foodAutonomy"] },
  { id: "practical", icon: "🛠", label: "Praktisch",
    criteria: ["internet", "water"] },
];

const PRESETS = {
  "Voor jullie": { ...DEFAULT_WEIGHTS },
  "Slow living": {
    ...DEFAULT_WEIGHTS,
    natureAccess: 10, tranquility: 10, foodAutonomy: 10, culturalAuthenticity: 9,
    climate: 7, dryness: 7, sunshine: 7,
    surf: 3, coffee: 3, wellness: 5,
  },
  "Surf-gezin": {
    ...DEFAULT_WEIGHTS,
    surf: 10, coffee: 8, wellness: 8,
    youngFamilies: 10, montessori: 8, forestSchool: 8,
    sunshine: 9, dryness: 8,
    tranquility: 5, culturalAuthenticity: 5,
  },
  "Reset": { ...DEFAULT_WEIGHTS },
};

function criterionById(id) { return CRITERIA.find(c => c.id === id); }

function FormatVal({ v }) {
  return <span className="val"><span className="val-num">{v}</span><small className="val-max">/10</small></span>;
}

const LEVELS = [
  { label: "Uit",        v: 0 },
  { label: "Beetje",     v: 4 },
  { label: "Belangrijk", v: 7 },
  { label: "Heel",       v: 10 },
];
function nearestLevel(v) {
  if (v <= 1) return 0;
  if (v <= 5) return 4;
  if (v <= 8) return 7;
  return 10;
}

function ImportanceRow({ c, value, onChange, disabled }) {
  if (!c) return null;
  const isDistance = c.id === "montessori" || c.id === "forestSchool";
  const isAuto = c.id === "dryness" || c.id === "sunshine" || c.id === "internet" || c.id === "water";
  const sel = nearestLevel(value);
  return (
    <div className={`imp-row ${disabled ? "is-disabled" : ""}`}>
      <div className="imp-head">
        <span className="imp-label">
          {c.label}
          {isDistance && <span className="crit-badge crit-badge-km">km</span>}
          {isAuto && <span className="crit-badge crit-badge-auto">auto</span>}
        </span>
      </div>
      <div className="imp-chips" role="group" aria-label={c.label}>
        {LEVELS.map(lv => (
          <button
            key={lv.v}
            type="button"
            className={`imp-chip${sel === lv.v ? " imp-chip-on" : ""}`}
            onClick={() => !disabled && onChange(lv.v)}
            disabled={disabled}
            aria-pressed={sel === lv.v}
          >{lv.label}</button>
        ))}
      </div>
    </div>
  );
}

function CategoryGroup({ group, weights, setWeight, defaultOpen, disabled }) {
  const [open, setOpen] = useState(defaultOpen);
  const ids = group.criteria;
  const adjustedCount = ids.filter(id => weights[id] !== DEFAULT_WEIGHTS[id]).length;
  return (
    <details className="cat-group" open={open} onToggle={e => setOpen(e.target.open)}>
      <summary>
        <span className="cat-icon">{group.icon}</span>
        <span className="cat-label">{group.label}</span>
        {adjustedCount > 0 && <span className="cat-badge">{adjustedCount} aangepast</span>}
        <span className="cat-count">{ids.length}</span>
        <span className="cat-chevron" aria-hidden>▾</span>
      </summary>
      <div className="cat-body">
        {ids.map(id => (
          <ImportanceRow key={id} c={criterionById(id)} value={weights[id] ?? 0}
                         onChange={(v) => setWeight(id, v)} disabled={disabled} />
        ))}
      </div>
    </details>
  );
}



// ─ Listing-filter componenten (patch #73 / #75) ────────────────
const LF_PRICE_MIN  = 0;
const LF_PRICE_MAX  = 3_000_000;
const LF_PRICE_STEP = 50_000;
const LF_AREA_MIN   = 0;
const LF_AREA_MAX   = 1_000_000;  // 100 ha in m²
const LF_AREA_STEP  = 500;        // 0.05 ha per stap

export const DEFAULT_LISTING_FILTERS = {
  priceMin: 0,
  priceMax: 3_000_000,
  areaMin:  0,
  areaMax:  1_000_000,
  bedroomsMin: 0,
};

function fmtPrice(v) {
  if (v <= 0) return "€0";
  if (v >= LF_PRICE_MAX) return "€3M+";
  if (v >= 1_000_000) return `€${(v / 1_000_000).toFixed(1).replace(".0", "")}M`;
  return `€${Math.round(v / 1_000)}k`;
}

function fmtArea(v) {
  if (v <= 0) return "0 m²";
  if (v >= LF_AREA_MAX) return "100+ ha";
  if (v < 1_000) return `${v} m²`;
  if (v < 10_000) {
    const ha = v / 10_000;
    const s = ha.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
    return `${s} ha`;
  }
  const ha = v / 10_000;
  return ha >= 10 ? `${Math.round(ha)} ha` : `${ha.toFixed(1)} ha`;
}

function DualRangeSlider({ label, min, max, step, valueMin, valueMax, onChangeMin, onChangeMax, formatFn }) {
  const pctMin = ((valueMin - min) / (max - min)) * 100;
  const pctMax = ((valueMax - min) / (max - min)) * 100;
  const zMin = pctMin > 60 ? 5 : 3;
  const zMax = pctMin > 60 ? 3 : 5;
  return (
    <div className="lf-row">
      <div className="lf-row-head">
        <span className="lf-label">{label}</span>
        <span className="lf-range-val">{formatFn(valueMin)} — {formatFn(valueMax)}</span>
      </div>
      <div className="lf-dual-range">
        <div className="lf-dual-track">
          <div className="lf-dual-fill" style={{ left: `${pctMin}%`, right: `${100 - pctMax}%` }} />
        </div>
        <input
          type="range" min={min} max={max} step={step} value={valueMin}
          className="lf-range"
          style={{ zIndex: zMin }}
          onChange={e => { const v = +e.target.value; if (v <= valueMax - step) onChangeMin(v); }}
          aria-label={`${label} minimum`}
        />
        <input
          type="range" min={min} max={max} step={step} value={valueMax}
          className="lf-range"
          style={{ zIndex: zMax }}
          onChange={e => { const v = +e.target.value; if (v >= valueMin + step) onChangeMax(v); }}
          aria-label={`${label} maximum`}
        />
      </div>
    </div>
  );
}

function BedroomChips({ value, onChange }) {
  const options = [
    { label: "Alle",  v: 0 },
    { label: "2+",    v: 2 },
    { label: "3+",    v: 3 },
    { label: "4+",    v: 4 },
  ];
  return (
    <div className="lf-row">
      <div className="lf-row-head">
        <span className="lf-label">Slaapkamers</span>
      </div>
      <div className="lf-chips">
        {options.map(o => (
          <button
            key={o.v}
            type="button"
            className={`lf-chip${value === o.v ? " lf-chip-active" : ""}`}
            onClick={() => onChange(o.v)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ListingFiltersSection({ filters, onChange }) {
  const f = filters || DEFAULT_LISTING_FILTERS;
  const isDefault =
    f.priceMin === 0 && f.priceMax >= LF_PRICE_MAX &&
    f.areaMin  === 0 && f.areaMax  >= LF_AREA_MAX  &&
    (f.bedroomsMin ?? 0) === 0;

  const activeCount =
    (f.priceMin > 0 || f.priceMax < LF_PRICE_MAX ? 1 : 0) +
    (f.areaMin  > 0 || f.areaMax  < LF_AREA_MAX  ? 1 : 0) +
    ((f.bedroomsMin ?? 0) > 0 ? 1 : 0);

  const set  = (k, v) => onChange({ ...f, [k]: v });
  const reset = () => onChange({ ...DEFAULT_LISTING_FILTERS });

  return (
    <details className="sb-listing-filters" open={!isDefault}>
      <summary>
        <span>
          🏠 Huizen filteren
          {activeCount > 0 && (
            <span className="cat-badge" style={{ marginLeft: 8 }}>{activeCount} actief</span>
          )}
        </span>
        <span className="cat-chevron" aria-hidden>▾</span>
      </summary>
      <div className="sb-listing-filters-body">
        <DualRangeSlider
          label="Prijs"
          min={LF_PRICE_MIN} max={LF_PRICE_MAX} step={LF_PRICE_STEP}
          valueMin={f.priceMin} valueMax={f.priceMax}
          onChangeMin={v => set("priceMin", v)}
          onChangeMax={v => set("priceMax", v)}
          formatFn={fmtPrice}
        />
        <DualRangeSlider
          label="Grondopp."
          min={LF_AREA_MIN} max={LF_AREA_MAX} step={LF_AREA_STEP}
          valueMin={f.areaMin} valueMax={f.areaMax}
          onChangeMin={v => set("areaMin", v)}
          onChangeMax={v => set("areaMax", v)}
          formatFn={fmtArea}
        />
        <BedroomChips
          value={f.bedroomsMin ?? 0}
          onChange={v => set("bedroomsMin", v)}
        />
        {!isDefault && (
          <button className="lf-reset-btn" onClick={reset} type="button">
            ↺ Reset huisfilters
          </button>
        )}
      </div>
    </details>
  );
}

export function Sidebar({
  listingFilters, onListingFiltersChange,
  weights, onWeightsChange,
  enforceVeto, onVetoChange,
  waterVeto = false, onWaterVetoChange = () => {},
  retreatMode, onRetreatModeChange,
  children, isOpen, canEdit = true
}) {
  const setWeight = (id, v) => onWeightsChange({ ...weights, [id]: v });
  const applyPreset = (name) => onWeightsChange({ ...PRESETS[name] });
  const reset = () => onWeightsChange({ ...DEFAULT_WEIGHTS });
  const [topMatchesOpen, setTopMatchesOpen] = useState(true);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [consensusMode, setConsensusMode] = useState(() => getProfiles().active === "consensus");
  const locked = consensusMode || !canEdit;

  const handleApplyProfile = (profileData) => {
    onWeightsChange({ ...profileData.weights });
    onVetoChange(profileData.enforceVeto);
    onRetreatModeChange(profileData.retreatMode);
    // Re-check consensus mode
    setTimeout(() => setConsensusMode(getProfiles().active === "consensus"), 50);
  };

  return (
    <aside className={`sidebar sidebar-left sidebar-v2 ${isOpen ? "open" : ""}`} aria-label="Criteria">

      <div className="sb-sticky-head">
        <div className="sb-brand-row">
          <span className="sb-brand-mark">PV</span>
          <div className="sb-brand-text">
            <strong>Portugal Verkenner</strong>
            <small>Filters &amp; criteria</small>
          </div>
        </div>

        <ProfileSwitcher
          weights={weights}
          enforceVeto={enforceVeto}
          retreatMode={retreatMode}
          onApplyProfile={handleApplyProfile}
        />

        <label className="sb-toggle">
          <input type="checkbox" checked={enforceVeto}
                 onChange={e => onVetoChange(e.target.checked)} disabled={locked} />
          <span className="sb-toggle-track" aria-hidden></span>
          <span className="sb-toggle-text">
            <b>Strikte criteria</b>
            <small>{enforceVeto ? "Veto's geactiveerd — falen wordt rood" : "Alles meetellen, ook plekken die niet voldoen"}</small>
          </span>
        </label>
              {/* Water veto toggle */}
              <label className="sb-toggle">
                <input type="checkbox" checked={waterVeto} onChange={e => onWaterVetoChange(e.target.checked)} />
                <span className="sb-toggle-track" />
                <span className="sb-toggle-text">
                  <b>💧 Water-zekerheid vereist</b>
                  <small>Locaties met extreme droogte (o.a. Aljezur, Tavira) worden uitgesloten</small>
                </span>
              </label>

        <label className="sb-toggle">
          <input type="checkbox" checked={retreatMode}
                 onChange={e => onRetreatModeChange(e.target.checked)} disabled={locked} />
          <span className="sb-toggle-track" aria-hidden></span>
          <span className="sb-toggle-text">
            <b>Retreat-business overwegen</b>
            <small>{retreatMode ? "Cashflow + AL-vergunning meegewogen" : "Pure 'voor onszelf'-scoring"}</small>
          </span>
        </label>
      </div>

      <div className="sb-presets-row">
        <span className="sb-presets-label">Snelle presets</span>
        <div className="sb-presets">
          {Object.keys(PRESETS).map(name => (
            <button key={name}
              className={`sb-preset-chip ${name === "Reset" ? "is-reset" : ""}`}
              onClick={() => applyPreset(name)} type="button"
              disabled={locked}>
              {name}
            </button>
          ))}
        </div>
      </div>

      <ListingFiltersSection
        filters={listingFilters || DEFAULT_LISTING_FILTERS}
        onChange={onListingFiltersChange || (() => {})}
      />

      <h2 className="section-v2">Pas filters aan</h2>
      <p className="section-hint">Kies per criterium hoe zwaar het voor jullie weegt.</p>

      <div className="cat-groups">
        {GROUPS.map((g, i) => (
          <CategoryGroup key={g.id} group={g} weights={weights} setWeight={setWeight}
                         defaultOpen={i === 0} disabled={locked} />
        ))}
      </div>

      <button className="sb-reset-btn" onClick={reset} type="button" disabled={locked}>
        ↺ Reset alle gewichten naar standaard
      </button>

      <details className="sb-checklist" open={checklistOpen} onToggle={e => setChecklistOpen(e.target.open)}>
        <summary>
          <span className="sb-checklist-icon">📋</span>
          <span className="sb-checklist-title">
            <b>Voor je gaat kijken</b>
            <small>Praktische checklist + juridische tips</small>
          </span>
          <span className="cat-chevron" aria-hidden>▾</span>
        </summary>
        <div className="sb-checklist-body">
          <BuyingChecklist embedded />
        </div>
      </details>
    </aside>
  );
}
